// compliance-exemption-card.component.ts
import { Component, Input, OnChanges } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CookieService } from 'ngx-cookie-service';
import { ComplianceExemptionService } from '../../services/compliance-exemption.service';
import { daysFromNow, startOfDayFromNow, toDateOnlyString, toLocalDateOnly } from '../../utils/compliance-exemption.util';

/** Admin-only control for a company's T&C / KYC exemption (spec §5.4). The API enforces admin; this only hides the UI. */
@Component({
  selector: 'app-compliance-exemption-card',
  templateUrl: './compliance-exemption-card.component.html',
  styleUrls: ['./compliance-exemption-card.component.css'],
})
export class ComplianceExemptionCardComponent implements OnChanges {
  @Input() userId!: number;
  @Input() alternateStyle = false;


  isAdmin = false;
  loading = false;
  saving = false;
  data: any = null;
  form = { planType: 'TRIAL', skipTerms: true, skipKyc: true, endsAt: daysFromNow(180) as Date | null, note: '' };
  restore = { terms: true, kyc: true };
  readonly minDate = startOfDayFromNow(1);
  readonly maxDate = daysFromNow(366);

  constructor(private api: ComplianceExemptionService, private snack: MatSnackBar, private cookies: CookieService) {
    try { this.isAdmin = JSON.parse(this.cookies.get('isAdmin') || 'false') === true || (this.cookies.get('userRole') || '').toLowerCase() === 'admin'; } catch { this.isAdmin = false; }
  }

  ngOnChanges(): void { if (this.isAdmin && this.userId) { this.load(); } }

  async load(): Promise<void> {
    this.loading = true;
    try {
      this.data = await this.api.getByUser(this.userId);
      const e = this.data?.exemption;
      if (e) {
        this.form = {
          planType: e.planType,
          skipTerms: Number(e.skipTerms) === 1,
          skipKyc: Number(e.skipKyc) === 1,
          endsAt: toLocalDateOnly(e.endsAtText),
          note: e.note || '',
        };
        this.restore = { terms: Number(e.skipTerms) === 1, kyc: Number(e.skipKyc) === 1 };
      }
    } catch {
      this.snack.open('Could not load the trial / subscription settings.', 'OK', { duration: 3000, panelClass: 'custom-snack' });
    } finally {
      this.loading = false;
    }
  }

  get active(): boolean { return !!this.data?.exemption && Number(this.data.exemption.isActive) === 1; }
  get endedOnDate(): Date | null { return toLocalDateOnly(this.data?.exemption?.endsAtText ?? null); }
  get savedSkipTerms(): boolean { return Number(this.data?.exemption?.skipTerms) === 1; }
  get savedSkipKyc(): boolean { return Number(this.data?.exemption?.skipKyc) === 1; }
  get trialEnded(): boolean { return !!this.data?.trialEnded && this.active && !this.data?.exemption?.endHandledAt; }

  onPlanChange(): void {
    if (this.form.planType === 'TRIAL' && !this.form.endsAt) { this.form.endsAt = daysFromNow(180); }
  }

  async save(): Promise<void> {
    this.saving = true;
    try {
      await this.api.upsert({
        userId: this.userId,
        planType: this.form.planType,
        skipTerms: !!this.form.skipTerms,
        skipKyc: !!this.form.skipKyc,
        endsAt: this.form.endsAt ? toDateOnlyString(this.form.endsAt) : null,
        note: this.form.note || null,
      });
      this.snack.open('Trial / subscription settings saved.', 'OK', { duration: 3000, panelClass: 'custom-snack' });
      await this.load();
    } catch (e: any) {
      this.snack.open(e?.error?.errors?.[0]?.message || 'Could not save the settings.', 'OK', { duration: 4000, panelClass: 'custom-snack' });
    } finally { this.saving = false; }
  }

  async turnOff(): Promise<void> {
    this.saving = true;
    try {
      await this.api.disable({ userId: this.userId });
      this.snack.open('Exemption turned off. Terms & Conditions and KYC now apply.', 'OK', { duration: 3000, panelClass: 'custom-snack' });
      await this.load();
    } catch (e: any) { this.snack.open(e?.error?.errors?.[0]?.message || 'Could not turn the exemption off.', 'OK', { duration: 3000, panelClass: 'custom-snack' }); }
    finally { this.saving = false; }
  }

  async restoreGates(): Promise<void> {
    if (!this.restore.terms && !this.restore.kyc) { return; }
    this.saving = true;
    try {
      const r: any = await this.api.restoreGates({ userId: this.userId, restoreTerms: this.restore.terms, restoreKyc: this.restore.kyc });
      this.snack.open(`Turned back on. ${r?.notified ?? 0} user(s) notified.`, 'OK', { duration: 3500, panelClass: 'custom-snack' });
      await this.load();
    } catch (e: any) { this.snack.open(e?.error?.errors?.[0]?.message || 'Could not turn the requirements back on.', 'OK', { duration: 3000, panelClass: 'custom-snack' }); }
    finally { this.saving = false; }
  }
}
